import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type InputFieldState =
  | "inactive"
  | "disabled"
  | "active"
  | "error"
  | "filled";

export type InputFieldSize = "default" | "compact";

interface InputFieldProps {
  label?: string;
  value?: string;
  placeholder?: string;
  type?: "text" | "password" | "email" | "number";
  disabled?: boolean;
  hasError?: boolean;
  hintText?: string;
  errorText?: string;
  maxLength?: number;
  fullWidth?: boolean;
  size?: InputFieldSize;
  onClear?: () => void;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * Input field with optional label, validation state, and clear/eye icon.
 */
export const InputField = ({
  label,
  value,
  placeholder,
  type = "text",
  disabled,
  hasError,
  hintText,
  errorText,
  maxLength,
  fullWidth = false,
  size = "default",
  onClear,
  onChange,
  onFocus,
  onBlur,
}: InputFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const isFilled = Boolean(value && value.length > 0);
  const resolvedErrorText = errorText ?? t("DesignComponents.DefaultError");

  const visualState = useMemo<InputFieldState>(() => {
    if (disabled) return "disabled";
    if (hasError) return "error";
    if (isFocused) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [disabled, hasError, isFocused, isFilled]);

  const shouldShowIcon = size !== "compact" && (disabled || isFilled);
  const iconName = disabled ? "v2/eye" : "v2/cross";
  const handleClear = onClear ?? (() => {});

  const handleFocus = (): void => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    onChange?.(event.target.value);
  };

  return (
    <Container
      aria-disabled={disabled}
      $fullWidth={fullWidth}
      $size={size}
    >
      {label && <Label>{label}</Label>}
      <FieldWrapper $state={visualState} $size={size}>
        <Input
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          $state={visualState}
          $size={size}
          $type={type}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {shouldShowIcon && (
          <IconWrapper
            role={!disabled && isFilled ? "button" : undefined}
            onClick={!disabled && isFilled ? handleClear : undefined}
            $clickable={!disabled && isFilled}
          >
            <CustomIcon
              name={iconName}
              size="24px"
              color={
                visualState === "error" ? ColorV2.NegativeRed : ColorV2.Blue5
              }
            />
          </IconWrapper>
        )}
      </FieldWrapper>
      {(visualState === "error" ? resolvedErrorText : hintText) && (
        <Message $state={visualState}>
          {visualState === "error" ? resolvedErrorText : hintText}
        </Message>
      )}
    </Container>
  );
};

const COMPACT_INPUT_HEIGHT_PX = 40;
const COMPACT_INPUT_WIDTH_PX = 115;
const COMPACT_INPUT_PADDING_X_PX = 12;

const Container = styled.div<{ $fullWidth: boolean; $size: InputFieldSize }>`
  width: ${(props) =>
    props.$fullWidth
      ? "100%"
      : props.$size === "compact"
        ? `${COMPACT_INPUT_WIDTH_PX}px`
        : `${V2_INPUT_WIDTH_PX}px`};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

const FieldWrapper = styled.div<{
  $state: InputFieldState;
  $size: InputFieldSize;
}>`
  display: flex;
  align-items: center;
  height: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_HEIGHT_PX}px`
      : `${V2_INPUT_HEIGHT_PX}px`};
  padding: 0
    ${(props) =>
      props.$size === "compact"
        ? `${COMPACT_INPUT_PADDING_X_PX}px`
        : "16px"};
  border-radius: ${(props) => (props.$size === "compact" ? "12px" : "16px")};
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}
`;

const Input = styled.input<{ $state: InputFieldState; $size: InputFieldSize; $type: string }>`
  ${TypographyV2.b2}
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};

  ${(props) =>
    props.$size === "compact" &&
    props.$type === "number" &&
    css`
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    `}

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$state === "active" &&
    css`
      color: ${ColorV2.Blue1};

      &::placeholder {
        color: ${ColorV2.Blue1};
      }
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};

      &::placeholder {
        color: ${ColorV2.NegativeRed};
      }
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      color: ${ColorV2.Blue5};
      cursor: not-allowed;
    `}

  ${(props) =>
    props.$state === "filled" &&
    css`
      color: ${ColorV2.Blue1};
    `}
`;

const IconWrapper = styled.span<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  ${(props) =>
    props.$clickable &&
    css`
      cursor: pointer;
    `}
`;

const Message = styled.span<{ $state: InputFieldState }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue5};

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};
    `}
`;
